'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCreateUser } from '@/hooks/use-users'
import { UserForm } from '@/components/partials/users'
import { type UserFormRef } from '@/components/partials/users/form'
import { CreateUserParams } from '@/types/user'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const userFormRef = useRef<UserFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createUser, isPending: isLoading } = useCreateUser()

  const handleSubmit = (data: CreateUserParams) => {
    createUser(data, {
      onSuccess: async newUser => {
        setIsRedirecting(true)
        queryClient.setQueryData(['user', newUser.id], newUser)
        await queryClient.invalidateQueries({ queryKey: ['users'] })
        toast.success(t('created_successfully'))
        router.push(`/users/${newUser.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateUserParams) => {
    createUser(data, {
      onSuccess: async newUser => {
        queryClient.setQueryData(['user', newUser.id], newUser)
        await queryClient.invalidateQueries({ queryKey: ['users'] })
        const detailUrl = `/users/${newUser.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        userFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    console.error('Error creating user:', error)

    if (userFormRef.current) {
      userFormRef.current.handleValidationErrors(error)
    }

    if (error.response?.status !== 422) {
      toast.error(t('error_creating'))
    }
  }

  const handleCancel = () => {
    router.push('/users')
  }

  return (
    <UserForm
      key={formKey}
      ref={userFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
